import Phaser from "phaser";
import { HealthBar } from "../ui/health-bar";
import { ArmorShieldBar } from "../ui/armor-shield-bar";
import { AttackCooldownBar } from "../ui/attack-cooldown-bar";
import { AimCooldownBar } from "../ui/aim-cooldown-bar";
import { COLORS, hexToPhaser } from "../ui/colors";
import { playSound } from "../utils/sound-utils";
import {
  Item,
  TILE_SIZE,
  PLAYER_UNARMED_ATTACK_INTERVAL,
  PlayerData,
  LIMBS_DEFAULT_VALUES,
  BASE_STATIONS_DEFAULT,
  getArmorItemsFromEquipment,
  getHatSpriteKey,
} from "./common";
import type { ArmorItemData } from "./common";
import { emoticonKeyToFrame } from "./season";

// Standardized player attack range
export const WEAPON_Y_OFFSET = 2; // Offset weapon and attack indicators down from player center
// Player sprite frame is 32px tall centered on the container origin, so the top of the head
// sits at local y = -16 regardless of the moving/idle animation state (see startMovingAnimation
// and stopMovingAnimation, which both keep the sprite's vertical span at -16..+16). Nudged down
// from that bare minimum so the hat visibly overlaps the head instead of floating above it.
export const HAT_Y_OFFSET = -4;
// Hat bobs down this many pixels while the second walk frame is shown, in sync
// with the 2-frame body animation, then back up on the first frame.
export const HAT_BOB_OFFSET = 1;
// While moving, the body and the hat hop this many pixels up and back down together,
// on top of the hat's own frame bob.
export const WALK_BOB_OFFSET = 2;
// Duration of one leg of that hop (ms); a full up-and-down cycle takes twice as long
// and lines up with the 2-frame walk animation at PLAYER_ANIM_FRAME_RATE.
const WALK_BOB_DURATION = 250;
// How long the hop takes to ease back down to rest when the player stops (ms), instead
// of snapping the body down from wherever mid-hop it happened to be.
const WALK_BOB_SETTLE_DURATION = 120;
// Duration of the idle breathing tween's scaleY squeeze, one direction (ms).
const IDLE_BREATH_DURATION = 1000;
// The body is a 2-frame animation that keeps playing while standing still and while
// walking, at the same rate as the base scene's raider preview so the hat (which bobs
// off the animation's frame changes) keeps the same pace in both scenes.
export const PLAYER_ANIM_FRAME_RATE = 4;
// Sprinting plays that same animation twice as fast, hat included.
export const SPRINT_ANIM_TIME_SCALE = 2;

export class Player {
  lastMoveDirection: { x: number; y: number } = { x: 0, y: 0 };
  data: PlayerData;
  scene: Phaser.Scene;

  // Callbacks for movement and damage events
  public onMoveCallback?: () => void;
  public onDamageCallback?: () => void;
  private movingTimeout?: ReturnType<typeof setTimeout>;
  private movingTimeoutDuration: number = 200;
  private isMoving: boolean = false;
  private idleBreathTween?: Phaser.Tweens.Tween;
  // Tweened up/down hop applied to the body and hat while moving.
  private walkBob = { offset: 0 };
  private walkBobTween?: Phaser.Tweens.Tween;
  private walkBobSettleTween?: Phaser.Tweens.Tween;
  // Body y the hop is measured from: 0 while moving (center origin), half a frame
  // while idle (bottom origin, see stopMovingAnimation).
  private spriteBaseY: number = 0;
  // Hat's own 1px bob off the body animation's frame changes.
  private hatFrameBob: number = 0;
  private sprinting: boolean = false;
  socketId: string;
  container: Phaser.GameObjects.Container;
  sprite: Phaser.GameObjects.Sprite;
  weaponSprite?: Phaser.GameObjects.Sprite;
  hatSprite?: Phaser.GameObjects.Sprite;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  health: number;
  maxHealth: number;
  armor: number = 0;
  maxArmor: number = 0;
  healthbar?: HealthBar;
  armorbar?: ArmorShieldBar;
  attackCooldownBars: AttackCooldownBar[] = [];
  aimCooldownBars: AimCooldownBar[] = [];
  attackAreaCircle?: Phaser.GameObjects.Graphics;
  squadIndicator?: Phaser.GameObjects.Graphics;
  dustSprite?: Phaser.GameObjects.Sprite;
  maxPredictionDistance: number;
  adminMode: boolean = false;
  lastAttackDirection: { x: number; y: number } = { x: 1, y: 0 };
  debugText?: Phaser.GameObjects.Text | null; // Allow null assignment

  constructor(
    socketId: string,
    scene: Phaser.Scene,
    x: number,
    y: number,
    health = 100,
    maxHealth = 100,
    hideHealthBarByDefault = false,
  ) {
    this.data = {
      trophies: 0,
      inventory: [],
      equipment: {},
      storage: [],
      lootbags: [],
      actions: [],
      recipes: [],
      rooms: [],
      stations: BASE_STATIONS_DEFAULT,
      credits: 0,
      name: socketId, // Default to id, will be overwritten by server
      killStreak: 0,
      accountMail: "",
      limbs: LIMBS_DEFAULT_VALUES,
      dailyExtractions: 0,
      nrExtractions: 0,
      mailVerified: false,
      admin: false,
    };
    this.socketId = socketId;
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.targetX = x;
    this.targetY = y;
    this.health = health;
    this.maxHealth = maxHealth;
    this.maxPredictionDistance = 8;
    // IMPORTANT: Make sure 'player' is loaded as a spritesheet in preload, e.g.:
    // scene.load.spritesheet('player', 'path/to/player.png', { frameWidth: ..., frameHeight: ... });
    this.sprite = scene.add.sprite(0, 0, "player");
    this.sprite.setDepth(1); // Above squad indicator
    // Bob the hat with the body's 2-frame walk animation
    this.sprite.on(
      Phaser.Animations.Events.ANIMATION_UPDATE,
      this.handleHatBobFrame,
      this,
    );
    // Add walking animation (frames 0 and 1)
    if (scene.anims && !scene.anims.exists("player-walk")) {
      scene.anims.create({
        key: "player-walk",
        frames: scene.anims.generateFrameNumbers("player", {
          start: 0,
          end: 1,
        }),
        frameRate: PLAYER_ANIM_FRAME_RATE,
        repeat: -1,
      });
    }
    // Only show name for other players (when hideHealthBarByDefault is true)
    const displayName = hideHealthBarByDefault ? this.data.name : undefined;
    this.armorbar = new ArmorShieldBar(scene, -20, -50, true);
    this.healthbar = new HealthBar(
      scene,
      -20,
      -40,
      40,
      6,
      this.maxHealth,
      hideHealthBarByDefault,
      displayName,
    );
    // Reposition name text above the armor bar when present
    if (this.healthbar.nameText) {
      this.healthbar.nameText.setPosition(-20 + 40 / 2, -45 - 6);
    }

    // Create weapon sprite (initially hidden, will be shown when weapon is equipped)
    this.weaponSprite = scene.add.sprite(0, WEAPON_Y_OFFSET, "weapon", 0);
    this.weaponSprite.setOrigin(0, 0.5); // Set origin to left side (0) and vertical center (0.5)
    this.weaponSprite.setVisible(false);
    this.weaponSprite.setDepth(3); // Above player sprite

    // Create hat sprite (initially hidden, will be shown when a hat cosmetic is equipped)
    this.hatSprite = scene.add.sprite(0, HAT_Y_OFFSET, "hat-1", 0);
    this.hatSprite.setOrigin(0.5, 1); // Bottom-center so it sits on top of the head
    this.hatSprite.setVisible(false);
    this.hatSprite.setDepth(4); // Above player sprite and weapon

    // Create dust sprite for sprint effect (initially hidden)
    // Only create if dust texture exists
    if (scene.textures.exists("dust")) {
      this.dustSprite = scene.add.sprite(0, 0, "dust", 0);
      this.dustSprite.setOrigin(0.5, 0.5);
      this.dustSprite.setVisible(false);
      this.dustSprite.setDepth(0); // Below player sprite
      this.dustSprite.setScale(1.5); // Make slightly larger for visibility
    }

    const containerChildren: Phaser.GameObjects.GameObject[] = [];
    if (this.dustSprite) {
      containerChildren.push(this.dustSprite);
    }
    containerChildren.push(
      this.sprite,
      this.weaponSprite,
      this.hatSprite,
      this.armorbar.bg,
      this.healthbar.bar,
      this.healthbar.overlay,
    );

    this.container = scene.add.container(x, y, containerChildren);
    if (this.healthbar.nameText) {
      this.container.add(this.healthbar.nameText);
    }
    // Set initial depth based on Y position for proper rendering order
    // Use 1000 + y to keep above static tile layers (0-2) but allow Y-sorting
    this.container.setDepth(1000 + y);
    this.container.setData("player", this);
    this.container.setData("socketId", socketId);
    this.attackAreaCircle = undefined;

    // Ensure active skin and equipped hat are shown on initialization
    this.updateSkin(this.data.activeSkin);
    this.updateHatSprite();

    // Start in the idle state so the breathing/hat-bob animation plays immediately,
    // instead of only kicking in after the player's first move-then-stop cycle.
    this.stopMovingAnimation();
  }

  /**
   * Bob the hat sprite down while the second walk frame is showing, back up on the first,
   * keeping it in sync with the body's 2-frame animation.
   */
  private handleHatBobFrame = (
    _animation: Phaser.Animations.Animation,
    frame: Phaser.Animations.AnimationFrame,
  ) => {
    this.hatFrameBob = frame.index === 2 ? HAT_BOB_OFFSET : 0;
    this.applyBob();
  };

  /**
   * Write the current walk hop (plus the hat's frame bob) onto the body and hat, so the
   * two offsets compose instead of overwriting each other.
   */
  private applyBob = () => {
    if (this.sprite && this.sprite.active) {
      this.sprite.y = this.spriteBaseY + this.walkBob.offset;
    }
    if (this.hatSprite && this.hatSprite.active) {
      this.hatSprite.y = HAT_Y_OFFSET + this.hatFrameBob + this.walkBob.offset;
    }
  };

  /**
   * Start the up/down hop that runs while the player is moving. Idempotent: repeated
   * move events keep the running tween instead of restarting it mid-hop.
   */
  private startWalkBob() {
    // A settle left over from a move that just ended would fight the loop, and the loop
    // has to start from a zero offset or its yoyo would swing around the wrong midpoint.
    this.clearWalkBobSettle();
    if (!this.walkBobTween) {
      this.walkBobTween = this.scene.tweens.add({
        targets: this.walkBob,
        offset: -WALK_BOB_OFFSET,
        duration: WALK_BOB_DURATION,
        ease: "Sine.easeInOut",
        yoyo: true,
        repeat: -1,
        onUpdate: this.applyBob,
      });
    }
    this.walkBobTween.timeScale = this.sprinting ? SPRINT_ANIM_TIME_SCALE : 1;
    // Write the resting position now: the tween's first onUpdate is a frame away and the
    // hat's frame bob only fires on animation frame changes, so without this the body
    // stays at the idle y - half a frame low, the origin having just moved back to
    // center - for anything up to a full animation frame.
    this.applyBob();
  }

  /**
   * Stop the walk hop and ease the body and hat back onto their resting positions.
   */
  private stopWalkBob() {
    if (this.walkBobTween) {
      this.walkBobTween.stop();
      this.walkBobTween.destroy();
      this.walkBobTween = undefined;
    }
    this.clearWalkBobSettle();
    this.applyBob();
    if (this.walkBob.offset === 0) return;
    this.walkBobSettleTween = this.scene.tweens.add({
      targets: this.walkBob,
      offset: 0,
      duration: WALK_BOB_SETTLE_DURATION,
      ease: "Sine.easeOut",
      onUpdate: this.applyBob,
      onComplete: () => {
        this.walkBobSettleTween = undefined;
      },
    });
  }

  private clearWalkBobSettle() {
    if (!this.walkBobSettleTween) return;
    this.walkBobSettleTween.stop();
    this.walkBobSettleTween.destroy();
    this.walkBobSettleTween = undefined;
    this.walkBob.offset = 0;
  }

  /**
   * Flip the player's body and hat sprites to face left/right, keeping accessories in sync
   * with the body instead of each caller flipping the body sprite on its own.
   */
  setFacing(flipped: boolean) {
    const scaleX = flipped ? -1 : 1;
    this.sprite.setScale(scaleX, 1);
    this.hatSprite?.setScale(scaleX, 1);
  }

  /**
   * Update the weapon sprite based on equipped weapon
   */
  updateWeaponSprite() {
    if (!this.weaponSprite || !this.weaponSprite.scene) return;

    const weapon: Item | undefined = this.data?.equipment?.Weapon;
    if (weapon && weapon.definition.spriteSheet === "weapon") {
      // Calculate frame index based on sprite coordinates
      const columns = 10; // weapon spritesheet has 10 columns
      const frame =
        weapon.definition.spriteY * columns + weapon.definition.spriteX;
      this.weaponSprite.setFrame(frame);
      this.weaponSprite.setVisible(true);
    } else {
      // No weapon equipped, hide sprite
      this.weaponSprite.setVisible(false);
    }
  }

  /**
   * Update the hat sprite based on the equipped hat skin
   */
  updateHatSprite() {
    if (!this.hatSprite || !this.hatSprite.scene) return;

    const textureKey = getHatSpriteKey(this.data?.equippedHat);
    if (textureKey && this.hatSprite.scene.textures.exists(textureKey)) {
      this.hatSprite.setTexture(textureKey);
      this.hatSprite.setVisible(true);
    } else {
      this.hatSprite.setVisible(false);
    }
  }

  /**
   * Update player skin sprite
   * @param skinId The skin texture key (e.g., 'skin1', 'skin2', or undefined for default 'player')
   */
  updateSkin(skinId?: string) {
    if (!this.sprite || !this.sprite.scene) return;

    const textureKey = skinId || "player";
    const scene = this.sprite.scene;

    // Check if texture exists
    if (!scene.textures.exists(textureKey)) {
      console.warn(
        `[Player] Skin texture '${textureKey}' not found, using default`,
      );
      return;
    }

    // Update sprite texture
    this.sprite.setTexture(textureKey);

    // Create or update walking animation for this skin
    const animKey = `${textureKey}-walk`;
    if (!scene.anims.exists(animKey)) {
      scene.anims.create({
        key: animKey,
        frames: scene.anims.generateFrameNumbers(textureKey, {
          start: 0,
          end: 1,
        }),
        frameRate: PLAYER_ANIM_FRAME_RATE,
        repeat: -1,
      });
    }

    // If currently playing an animation, switch to the new skin's animation
    if (this.sprite.anims.isPlaying) {
      this.sprite.play(animKey);
    }
  }

  /**
   * Rotate weapon sprite to point in a direction
   * @param direction Direction vector {x, y}
   */
  rotateWeapon(direction: { x: number; y: number }) {
    if (!this.weaponSprite || !this.weaponSprite.visible) return;

    const angle = Math.atan2(direction.y, direction.x);

    // Flip weapon sprite vertically if pointing left to avoid upside-down appearance
    if (direction.x < 0) {
      this.weaponSprite.setScale(1, -1);
      // When flipped, we need to subtract the angle offset instead
      this.weaponSprite.setRotation(angle - Math.PI / 4);
    } else {
      this.weaponSprite.setScale(1, 1);
      // Add 45 degrees (π/4 radians) to correct for sprite being drawn pointing up-right
      this.weaponSprite.setRotation(angle + Math.PI / 4);
    }
  }

  /**
   * Rotate weapon to point at world coordinates (for mouse following)
   * @param targetX World X coordinate
   * @param targetY World Y coordinate
   */
  rotateWeaponToPoint(targetX: number, targetY: number) {
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 0.01) {
      const direction = { x: dx / dist, y: dy / dist };
      this.rotateWeapon(direction);
    }
  }

  /**
   * Show a green circle around this player to indicate squad membership
   */
  showSquadIndicator() {
    if (this.squadIndicator) {
      return; // Already showing
    }

    const graphics = this.scene.add.graphics();
    graphics.lineStyle(4, hexToPhaser(COLORS.GREEN)); // Green color
    graphics.strokeEllipse(0, 12, 40, 28); // Ellipse around player - wider horizontally than vertically
    this.squadIndicator = graphics;
    this.container.addAt(graphics, 0); // Add at beginning so it renders behind sprite
  }

  /**
   * Hide the squad indicator
   */
  hideSquadIndicator() {
    if (this.squadIndicator) {
      this.squadIndicator.destroy();
      this.squadIndicator = undefined;
    }
  }
  /**
   * Spawn a dust particle at the player's current position
   */
  spawnDustParticle() {
    if (!this.dustSprite || !this.scene.textures.exists("dust")) return;

    // Create animation if it doesn't exist yet
    if (this.scene.anims && !this.scene.anims.exists("dust-anim")) {
      this.scene.anims.create({
        key: "dust-anim",
        frames: this.scene.anims.generateFrameNumbers("dust", {
          start: 0,
          end: 2,
        }),
        frameRate: 8,
        repeat: 0, // Don't repeat, play once
      });
    }

    // Create a new dust sprite at current position
    const dust = this.scene.add.sprite(this.x, this.y, "dust", 0);
    dust.setOrigin(0.5, 0.5);
    dust.setDepth(0); // Below player sprite
    dust.setScale(1.5);

    // Ignore dust particle on UI camera (only render on main camera)
    const mainScene = this.scene as any;
    if (mainScene.uiCamera) {
      mainScene.uiCamera.ignore(dust);
    }

    if (this.scene.anims.exists("dust-anim")) {
      dust.play("dust-anim");
      // Play step sound effect when dust particle appears
      playSound(this.scene, "step");

      // Remove dust sprite after animation completes (375ms for 3 frames at 8 fps)
      this.scene.time.delayedCall(375, () => {
        dust.destroy();
      });
    }
  }

  /**
   * Start the dust sprint animation (for compatibility, calls spawnDustParticle)
   */
  startDustAnimation() {
    this.spawnDustParticle();
  }

  /**
   * Show an emoticon above the player's head: an animated speech bubble pops in,
   * with the selected emoticon face shown as a static image on top 500ms later
   * (so it syncs with the bubble animation). The bubble animation then pauses on
   * its middle frame for 2 seconds while the face is held on screen, before
   * resuming to play the pop-out and clean itself up.
   */
  showEmoji(emoticonKey: string): void {
    // Remove any existing emoticon display
    const existing = this.container.getByName("emoticonDisplay") as Phaser.GameObjects.Container | null;
    if (existing) {
      existing.destroy();
    }

    if (!this.scene.textures.exists("bubble-animation") || !this.scene.textures.exists("emoticons")) return;

    const BUBBLE_TOTAL_FRAMES = 20; // 5 cols × 4 rows
    const BUBBLE_HOLD_FRAME = Math.floor(BUBBLE_TOTAL_FRAMES / 2); // 1-based frame to pause on
    const BUBBLE_HOLD_MS = 2000;

    // Register the bubble pop-in/out animation once, shared by every emoticon.
    const animKey = "emote-bubble-pop";
    if (!this.scene.anims.exists(animKey)) {
      this.scene.anims.create({
        key: animKey,
        frames: this.scene.anims.generateFrameNumbers("bubble-animation", {
          start: 0,
          end: BUBBLE_TOTAL_FRAMES - 1,
        }),
        frameRate: 10,
        repeat: 0,
      });
    }

    const display = this.scene.add.container(0, -72);
    display.setName("emoticonDisplay");
    this.container.add(display);

    const bubble = this.scene.add.sprite(0, 0, "bubble-animation", 0);
    bubble.setOrigin(0.5);
    bubble.setScale(2);
    display.add(bubble);

    const face = this.scene.add.sprite(0, 0, "emoticons", emoticonKeyToFrame(emoticonKey));
    face.setOrigin(0.5);
    face.setDisplaySize(30, 30);
    // Hold the face back so it lands after the bubble has popped in
    face.setVisible(false);
    display.add(face);

    // Play the bubble animation
    bubble.play(animKey);

    this.scene.time.delayedCall(500, () => {
      if (!face || !face.scene) return;
      face.setVisible(true);
    });

    // Pause the bubble halfway through and hold it there, so the emoticon stays
    // readable, then resume so the second half plays as the pop-out.
    let held = false;
    bubble.on(
      Phaser.Animations.Events.ANIMATION_UPDATE,
      (_anim: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
        if (held || frame.index < BUBBLE_HOLD_FRAME) return;
        held = true;
        bubble.anims.pause();

        this.scene.time.delayedCall(BUBBLE_HOLD_MS, () => {
          if (!bubble || !bubble.scene) return;
          // The face has had its time on screen; let the bubble close alone.
          if (face && face.scene) face.setVisible(false);
          bubble.anims.resume();
        });
      },
    );

    // Once the pop-out has played, fade the whole display away
    bubble.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      if (!display || !display.scene) return;
      this.scene.tweens.add({
        targets: display,
        alpha: 0,
        y: -88,
        duration: 400,
        onComplete: () => {
          if (display && display.scene) display.destroy();
        },
      });
    });
  }

  /**
   * Set callback to be called when player moves
   */
  setOnMoveCallback(callback: () => void) {
    this.onMoveCallback = callback;
  }

  /**
   * Set callback to be called when player takes damage
   */
  setOnDamageCallback(callback: () => void) {
    this.onDamageCallback = callback;
  }

  /**
   * Instantly set player position (used for reconnect correction)
   */
  setPositionInstant(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.targetX = x;
    this.targetY = y;
    this.container.setPosition(x, y);
    // Update depth based on Y position for proper rendering order
    // Use 1000 + y to keep above static tile layers (0-2) but allow Y-sorting
    this.container.setDepth(1000 + y);
    this.updateSquadIndicatorDepth();
  }

  setPosition(x: number, y: number, mapHandler?: any) {
    // Prevent movement if blocked by tile-objects-block
    if (mapHandler && mapHandler.isBlocked && mapHandler.isBlocked(x, y)) {
      return; // Do not update position if blocked
    }

    // Check if there's actually movement happening
    const isActualMovement =
      Math.abs(this.targetX - x) > 0.1 || Math.abs(this.targetY - y) > 0.1;

    // Update lastMoveDirection based on intended movement
    const dx = x - this.x;
    const dy = y - this.y;
    let dirX = 0,
      dirY = 0;
    if (Math.abs(dx) > Math.abs(dy)) {
      dirX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    } else if (Math.abs(dy) > 0) {
      dirY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
    }
    // If both dx and dy are nonzero, prefer the larger movement axis
    if (dirX !== 0 && dirY !== 0) {
      if (Math.abs(dx) >= Math.abs(dy)) {
        dirY = 0;
      } else {
        dirX = 0;
      }
    }
    if (dirX !== 0 || dirY !== 0) {
      this.lastMoveDirection = { x: dirX, y: dirY };
    }

    this.targetX = x;
    this.targetY = y;
    const tdx = this.targetX - this.x;
    const tdy = this.targetY - this.y;
    const dist = Math.sqrt(tdx * tdx + tdy * tdy);
    if (dist > this.maxPredictionDistance) {
      const ratio = this.maxPredictionDistance / dist;
      this.targetX = this.x + tdx * ratio;
      this.targetY = this.y + tdy * ratio;
    }
    // Start moving animation and reset timeout
    this.startMovingAnimation();

    // Trigger move callback if there's actual movement
    if (isActualMovement && this.onMoveCallback) {
      this.onMoveCallback();
    }
  }

  /**
   * Get the walk animation key based on current skin
   */
  private getWalkAnimKey(): string {
    const skinId = this.data?.activeSkin || "player";
    return `${skinId}-walk`;
  }

  public startMovingAnimation() {
    this.isMoving = true;
    if (this.idleBreathTween) {
      this.idleBreathTween.stop();
      this.idleBreathTween.destroy();
      this.idleBreathTween = undefined;
      this.sprite.setScale(this.sprite.scaleX, 1);
    }
    // Hop from the centered resting position while walking; the origin has to move back
    // with it, since the idle breathing parks it at the feet.
    this.sprite.setOrigin(0.5, 0.5);
    this.spriteBaseY = 0;
    this.startWalkBob();
    const animKey = this.getWalkAnimKey();
    if (
      this.sprite.anims &&
      (!this.sprite.anims.isPlaying || this.sprite.anims.getName() !== animKey)
    ) {
      this.sprite.anims.play(animKey, true);
    }
    if (this.movingTimeout) {
      clearTimeout(this.movingTimeout);
    }
    this.movingTimeout = setTimeout(() => {
      this.stopMovingAnimation();
    }, this.movingTimeoutDuration);
  }

  public stopMovingAnimation() {
    if (!this.sprite || !this.sprite.scene || !this.sprite.active) {
      return;
    }
    this.isMoving = false;
    // Standing still keeps the 2-frame animation running at the normal (non-sprint) rate,
    // like the base scene's raider preview, so the hat carries on bobbing in step with it.
    this.setSprinting(false);
    if (this.sprite && this.sprite.anims) {
      this.sprite.anims.play(this.getWalkAnimKey(), true);
    }
    if (this.idleBreathTween) {
      this.idleBreathTween.stop();
      this.idleBreathTween.destroy();
      this.idleBreathTween = undefined;
      this.sprite.setScale(this.sprite.scaleX, 1);
    }
    // Set origin to bottom-center so scaleY squeezes upward from the feet
    const frameH = this.sprite.displayHeight;
    this.sprite.setOrigin(0.5, 1);
    this.spriteBaseY = frameH / 2;
    this.stopWalkBob();
    this.idleBreathTween = this.scene.tweens.add({
      targets: this.sprite,
      scaleY: 0.93,
      duration: IDLE_BREATH_DURATION,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Toggle the sprint animation pace: the body (and with it the hat, which bobs off the
   * body's frame changes) plays the same 2-frame animation twice as fast while running.
   */
  public setSprinting(sprinting: boolean) {
    if (this.sprinting === sprinting) return;
    this.sprinting = sprinting;
    if (this.sprite && this.sprite.anims) {
      this.sprite.anims.timeScale = sprinting ? SPRINT_ANIM_TIME_SCALE : 1;
    }
    if (this.walkBobTween) {
      this.walkBobTween.timeScale = sprinting ? SPRINT_ANIM_TIME_SCALE : 1;
    }
  }

  // Client should NOT apply movement locally for server-authoritative movement.
  // Instead, only interpolate between last known position and latest server position.
  // Example usage:
  //   On receiving server update: player.setPosition(serverX, serverY);
  //   In game loop: player.interpolateToTarget(delta); // delta in seconds

  interpolateToTarget(delta: number) {
    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    // Only flip sprite for server corrections or other players
    // (local player flipping is handled in controls for better responsiveness)
    if (dx < -0.1) {
      this.setFacing(true);
    } else if (dx > 0.1) {
      this.setFacing(false);
    }
    if (dist > 1.0) {
      // Use delta time for frame-rate independent interpolation
      // Interpolation speed should be high enough to smoothly follow the 50ms movement updates
      // At 2.5 pixels per 50ms = 50 pixels/sec, we use a higher multiplier for smooth catch-up
      const interpolationSpeed = 30;
      const alpha = Math.min(interpolationSpeed * delta, 1.0);
      this.x += dx * alpha;
      this.y += dy * alpha;
    } else {
      this.x = this.targetX;
      this.y = this.targetY;
    }
    this.container.setPosition(this.x, this.y);
    // Update depth based on Y position for proper rendering order
    // Use 1000 + y to keep above static tile layers (0-2) but allow Y-sorting
    this.container.setDepth(1000 + this.y);
    this.updateSquadIndicatorDepth();
  }

  private updateSquadIndicatorDepth() {
    if (this.squadIndicator) {
      // Use world depth just like the player sprite, but offset by -1 to keep it behind
      this.squadIndicator.setDepth(1000 + this.y - 1);
    }
  }

  updateHealth(
    health: number,
    maxHealth: number,
    armor?: number,
    maxArmor?: number,
    armorItems?: ArmorItemData[],
  ) {
    const prevHealth = this.health;
    this.health = health;
    this.maxHealth = maxHealth;
    if (this.healthbar) {
      this.healthbar.setHealth(this.health);
    }
    if (armor !== undefined) this.armor = armor;
    if (maxArmor !== undefined) this.maxArmor = maxArmor;

    if (armorItems !== undefined) {
      if (this.armorbar) {
        if (armorItems.length > 0) {
          this.armorbar.setArmorItems(armorItems);
          this.armorbar.show();
        } else {
          this.armorbar.hide();
        }
      }
    } else {
      this.updateArmorBar();
    }

    if (this.sprite.scene && health < prevHealth) {
      if (this.onDamageCallback) {
        this.onDamageCallback();
      }
    }
  }

  updateArmorBar() {
    if (!this.armorbar) return;
    const equipment = this.data?.equipment;
    if (!equipment) {
      this.armorbar.hide();
      return;
    }
    const items = getArmorItemsFromEquipment(equipment);
    if (items.length > 0) {
      this.armorbar.setArmorItems(items);
      this.armorbar.show();
    } else {
      this.armorbar.hide();
    }
  }

  // drawHealthBar is no longer needed; healthbar is part of container
  isAlive(): boolean {
    return this.health > 0;
  }

  setAdminMode(isAdmin: boolean) {
    this.adminMode = isAdmin;
    // Set sprite transparency based on admin mode
    if (this.sprite) {
      this.sprite.setAlpha(isAdmin ? 0.5 : 1.0);
    }

    // Add debug text for player tile position
    if (isAdmin) {
      if (!this.debugText) {
        this.debugText = this.sprite.scene.add.text(0, 0, "", {
          font: "12px Arial",
          color: COLORS.WHITE, // Use valid color property
        });
        this.debugText.setDepth(1001);
        // Ignore debug text on UI camera (only render on main camera)
        const mainScene = this.sprite.scene as any;
        if (mainScene.uiCamera) {
          mainScene.uiCamera.ignore(this.debugText);
        }
      }
      const updateDebugText = () => {
        const tileX = Math.floor(this.x / TILE_SIZE);
        const tileY = Math.floor(this.y / TILE_SIZE);
        if (this.debugText) {
          this.debugText.setText(`Tile: (${tileX}, ${tileY})`);
          this.debugText.setPosition(this.x - 35, this.y - 55); // Position above the player sprite
        }
      };
      this.sprite.scene.events.on("update", updateDebugText);
    } else if (this.debugText) {
      this.debugText.destroy();
      this.debugText = null;
    }
  }

  public createAttackCooldownIndicator(scene: Phaser.Scene) {
    const bar = new AttackCooldownBar(scene, -20, -30, 40, 3);
    this.attackCooldownBars.push(bar);
    this.container.add(bar.bar);
    let startTime = Date.now();
    // Get equipped weapon's attack interval if available
    let duration = PLAYER_UNARMED_ATTACK_INTERVAL;
    const weapon: Item | undefined = this.data?.equipment?.Weapon;
    if (
      weapon &&
      weapon.definition.stats &&
      weapon.definition.stats.attackInterval
    ) {
      duration = weapon.definition.stats.attackInterval;
    }
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      bar.setProgress(progress);
      if (progress < 1) {
        bar.bar.setVisible(true);
        scene.time.delayedCall(16, animate);
      } else {
        // Wait for flash animation to finish, then destroy
        scene.time.delayedCall(350, () => {
          bar.destroy();
          const idx = this.attackCooldownBars.indexOf(bar);
          if (idx !== -1) this.attackCooldownBars.splice(idx, 1);
        });
      }
    };
    animate();
  }

  public createAimCooldownIndicator(scene: Phaser.Scene) {
    const bar = new AimCooldownBar(scene, -20, -20, 40, 3);
    this.aimCooldownBars.push(bar);
    this.container.add(bar.bar);
    let startTime = Date.now();
    // Get equipped weapon's aim delay if available
    let duration = 0;
    const weapon: Item | undefined = this.data?.equipment?.Weapon;
    if (weapon && weapon.definition.stats && weapon.definition.stats.aimDelay) {
      duration = weapon.definition.stats.aimDelay; // Already in milliseconds
    }
    if (duration === 0) {
      // No aim delay, don't show the bar
      bar.destroy();
      const idx = this.aimCooldownBars.indexOf(bar);
      if (idx !== -1) this.aimCooldownBars.splice(idx, 1);
      return;
    }
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      bar.setProgress(progress);
      if (progress < 1) {
        bar.bar.setVisible(true);
        scene.time.delayedCall(16, animate);
      } else {
        // Wait for flash animation to finish, then destroy
        scene.time.delayedCall(350, () => {
          bar.destroy();
          const idx = this.aimCooldownBars.indexOf(bar);
          if (idx !== -1) this.aimCooldownBars.splice(idx, 1);
        });
      }
    };
    animate();
  }

  destroy() {
    if (this.movingTimeout) {
      clearTimeout(this.movingTimeout);
      this.movingTimeout = undefined;
    }
    // Stop idle breath tween
    if (this.idleBreathTween) {
      this.idleBreathTween.stop();
      this.idleBreathTween.destroy();
      this.idleBreathTween = undefined;
    }
    // Stop the walk hop tweens
    if (this.walkBobTween) {
      this.walkBobTween.stop();
      this.walkBobTween.destroy();
      this.walkBobTween = undefined;
    }
    this.clearWalkBobSettle();
    // Destroy attack cooldown bars
    if (this.attackCooldownBars && Array.isArray(this.attackCooldownBars)) {
      this.attackCooldownBars.forEach((bar) => {
        if (bar && typeof bar.destroy === "function") bar.destroy();
      });
      this.attackCooldownBars = [];
    }
    // Destroy aim cooldown bars
    if (this.aimCooldownBars && Array.isArray(this.aimCooldownBars)) {
      this.aimCooldownBars.forEach((bar) => {
        if (bar && typeof bar.destroy === "function") bar.destroy();
      });
      this.aimCooldownBars = [];
    }
    // Destroy squad indicator
    if (
      this.squadIndicator &&
      typeof this.squadIndicator.destroy === "function"
    ) {
      this.squadIndicator.destroy();
      this.squadIndicator = undefined;
    }
    // Destroy armorbar
    if (this.armorbar && typeof this.armorbar.destroy === "function") {
      this.armorbar.destroy();
      this.armorbar = undefined;
    }
    // Destroy healthbar
    if (this.healthbar && typeof this.healthbar.destroy === "function") {
      this.healthbar.destroy();
      this.healthbar = undefined;
    }
    // Destroy weapon sprite
    if (this.weaponSprite && typeof this.weaponSprite.destroy === "function") {
      this.weaponSprite.destroy();
      this.weaponSprite = undefined;
    }
    // Destroy hat sprite
    if (this.hatSprite && typeof this.hatSprite.destroy === "function") {
      this.hatSprite.destroy();
      this.hatSprite = undefined;
    }
    // Remove and destroy all children of container
    if (
      this.container &&
      this.container.list &&
      Array.isArray(this.container.list)
    ) {
      this.container.list.forEach((child) => {
        if (child && typeof child.destroy === "function") child.destroy();
      });
      this.container.removeAll(true);
    }
    // Remove container from scene display list
    if (this.container && this.container.scene) {
      this.container.scene.children.remove(this.container);
    }
    // Destroy container
    if (this.container && typeof this.container.destroy === "function") {
      this.container.destroy();
      this.container = null as any;
    }
    // Remove and destroy sprite from scene display list
    if (this.sprite && this.sprite.scene) {
      this.sprite.scene.children.remove(this.sprite);
    }
    if (this.sprite && typeof this.sprite.destroy === "function") {
      this.sprite.destroy();
      this.sprite = null as any;
    }
    // Remove attack area circle if present
    if (
      this.attackAreaCircle &&
      typeof this.attackAreaCircle.destroy === "function"
    ) {
      this.attackAreaCircle.destroy();
      this.attackAreaCircle = undefined;
    }
    // Optionally show splash particle
    if (this.x !== undefined && this.y !== undefined) {
      import("../ui/splash-particle")
        .then((mod) => {
          if (this.sprite && this.sprite.scene) {
            mod.showSplashParticle(this.sprite.scene, this.x, this.y);
          } else if (this.container && this.container.scene) {
            mod.showSplashParticle(this.container.scene, this.x, this.y);
          }
        })
        .catch(() => {});
    }
  }
}
